import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, ArrowUpDown } from "lucide-react";

export default function PipelineRegistryPage() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [domainFilter, setDomainFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => { api.get("/pipelines").then((r) => { setPipelines(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const domains = [...new Set(pipelines.map((p) => p.domain))];
  const filtered = pipelines
    .filter((p) => (statusFilter === "all" || p.status === statusFilter) && (domainFilter === "all" || p.domain === domainFilter) && (p.name.toLowerCase().includes(search.toLowerCase()) || p.owner.toLowerCase().includes(search.toLowerCase()) || p.source_system.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => { const av = a[sortField], bv = b[sortField]; const d = sortDir === "asc" ? 1 : -1; return typeof av === "number" ? (av - bv) * d : String(av || "").localeCompare(String(bv || "")) * d; });

  const toggleSort = (f) => { if (sortField === f) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortField(f); setSortDir("asc"); } };

  const SortHeader = ({ field, children }) => (
    <TableHead className="cursor-pointer select-none hover:text-cyan-400 transition-colors" onClick={() => toggleSort(field)}>
      <div className="flex items-center gap-1">{children}<ArrowUpDown className="w-3 h-3 text-slate-600" /></div>
    </TableHead>
  );

  if (loading) return (<div className="p-6 space-y-4" data-testid="pipeline-registry-loading"><Skeleton className="h-8 w-48 bg-white/5" />{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 bg-white/5" />)}</div>);

  return (
    <div className="p-6 space-y-4" data-testid="pipeline-registry-page">
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Pipeline Registry</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{pipelines.length} pipelines monitored</p>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="pipeline-filters">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Search pipelines..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 bg-black/[0.02] border-gray-200 text-[#2D3142] placeholder:text-slate-600 focus:border-cyan-500/50" data-testid="pipeline-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200"><SelectItem value="all">All Status</SelectItem><SelectItem value="healthy">Healthy</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
        </Select>
        <Select value={domainFilter} onValueChange={setDomainFilter}>
          <SelectTrigger className="w-[140px] h-9 bg-black/[0.02] border-gray-200 text-slate-300" data-testid="domain-filter"><SelectValue placeholder="Domain" /></SelectTrigger>
          <SelectContent className="bg-[#0F1729] border-gray-200"><SelectItem value="all">All Domains</SelectItem>{domains.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Badge className="text-[9px] font-mono border-0 rounded-md" style={{ background: "rgba(30,58,95,0.1)", color: "#1E3A5F" }}>{filtered.length} results</Badge>
      </div>

      <div className="sf-card overflow-hidden relative" data-testid="pipeline-table">
        <Table className="data-table">
          <TableHeader>
            <TableRow className="bg-black/[0.01] border-b border-gray-200">
              <SortHeader field="name">Pipeline</SortHeader>
              <SortHeader field="source_system">Source</SortHeader>
              <SortHeader field="destination_asset">Destination</SortHeader>
              <SortHeader field="owner">Owner</SortHeader>
              <TableHead className="text-cyan-400">Schedule</TableHead>
              <SortHeader field="sla_minutes">SLA</SortHeader>
              <SortHeader field="status">Status</SortHeader>
              <SortHeader field="freshness_lag_minutes">Lag</SortHeader>
              <SortHeader field="row_count_delta">Row Delta</SortHeader>
              <TableHead className="text-cyan-400">Schema</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p, i) => (
              <TableRow key={p.pipeline_id} className="border-b border-white/[0.04] hover:bg-cyan-500/[0.03] transition-colors animate-fade-up" style={{ animationDelay: `${i * 30}ms` }} data-testid={`pipeline-row-${p.pipeline_id}`}>
                <TableCell>
                  <Link to={`/pipelines/${p.pipeline_id}`} className="font-medium hover:underline font-mono text-sm transition-colors" style={{ color: "#1E3A5F" }} data-testid={`pipeline-link-${p.pipeline_id}`}>{p.name}</Link>
                </TableCell>
                <TableCell className="text-sm text-slate-400">{p.source_system}</TableCell>
                <TableCell className="mono-data text-slate-400">{p.destination_asset}</TableCell>
                <TableCell className="text-sm text-slate-400">{p.owner}</TableCell>
                <TableCell className="mono-data text-slate-500">{p.schedule}</TableCell>
                <TableCell className="mono-data text-slate-300">{p.sla_minutes}m</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <span className={`status-dot ${p.status}`} />
                    <Badge className={`text-[9px] uppercase font-bold border-0 ${p.status === "critical" ? "bg-red-500/20 text-red-400" : p.status === "warning" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{p.status}</Badge>
                  </div>
                </TableCell>
                <TableCell className={`mono-data ${p.freshness_lag_minutes > p.sla_minutes ? "text-red-400 font-bold" : "text-slate-400"}`}>{p.freshness_lag_minutes}m</TableCell>
                <TableCell className={`mono-data ${p.row_count_delta < -10 ? "text-red-400 font-bold" : p.row_count_delta > 5 ? "text-amber-400" : "text-slate-400"}`}>{p.row_count_delta > 0 ? "+" : ""}{p.row_count_delta}%</TableCell>
                <TableCell className="mono-data text-slate-500">{p.schema_version}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-600 font-mono">[ No pipelines match filters ]</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
